import * as React from "react"
import { type Transaction } from "@digits-graphql/frontend/graphql-bearer"
import { BULK_STATUS_UPDATE_LIMIT } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/types"
import {
  type BulkUpdate,
  type TableVariant,
  type TransactionSelections,
  type TransactionUpdate,
  type TransactionUpdates,
} from "src/frontend/components/Shared/Transactions/types"
import { BulkTransactionUpdates } from "src/frontend/components/Shared/Transactions/Updates/Headers/BulkTransactionUpdates"
import { TransactionsUpdatesHeader } from "src/frontend/components/Shared/Transactions/Updates/Headers/TransactionsUpdatesHeader"
import {
  TransactionsUpdatesContext,
  type TransactionsUpdatesContextProps,
} from "src/frontend/components/Shared/Transactions/Updates/TransactionsUpdatesContext"
import { useAllowTransactionsTableEdits } from "src/frontend/components/Shared/Transactions/Updates/useAllowTransactionsTableEdits"
import {
  useApplyBulkUpdates,
  useApplyTransactionUpdates,
} from "src/frontend/components/Shared/Transactions/Updates/useApplyTransactionUpdates"
import { useUserSettings } from "src/frontend/hooks/useUserSettings"

interface TableProps {
  variant?: TableVariant
}

export const WithTransactionsUpdates: React.FC<React.PropsWithChildren<TableProps>> = ({
  variant,
  children,
}) => {
  // Disabling edits on Mobile until typeahead and UIs are responsive
  const { allowEdits, allowBulkEdits } = useAllowTransactionsTableEdits()

  const [updating, startUpdate] = React.useTransition()
  const {
    settings: { editLedgerEnabled: editing },
  } = useUserSettings()

  const {
    isUpdatingInline,
    transactionUpdates,
    getTransactionUpdate,
    updateTransaction,
    clearTransactionUpdates,
  } = useTransactionUpdates()
  const onAppyUpdates = useApplyTransactionUpdates()
  const applyTransactionUpdates = React.useCallback(() => {
    startUpdate(() => onAppyUpdates(transactionUpdates).then(clearTransactionUpdates))
  }, [clearTransactionUpdates, onAppyUpdates, transactionUpdates])

  const {
    isBulkSelecting,
    selectedTransactions,
    selectTransaction,
    unselectTransaction,
    toggleTransaction,
    clearSelections,
  } = useBulkUpdates()
  const onAppyBulkUpdates = useApplyBulkUpdates()
  const applyBulkUpdates = React.useCallback(
    (update: BulkUpdate) => {
      startUpdate(() => onAppyBulkUpdates(selectedTransactions, update).then(clearSelections))
    },
    [clearSelections, onAppyBulkUpdates, selectedTransactions]
  )

  const clearUpdates = React.useCallback(() => {
    clearTransactionUpdates()
    clearSelections()
  }, [clearSelections, clearTransactionUpdates])

  // Clear if edit mode changes
  React.useEffect(() => {
    if (!editing) {
      clearUpdates()
    }
  }, [clearUpdates, editing])

  // Clear if variant changes
  React.useEffect(() => {
    clearUpdates()
  }, [clearUpdates, variant])

  const context = React.useMemo<TransactionsUpdatesContextProps>(
    () => ({
      editable: allowEdits,
      editing,
      updating,
      clearUpdates,
      // Transaction Updates
      allowInlineUpdates: allowEdits && !isBulkSelecting && !updating,
      isUpdatingInline,
      transactionUpdates,
      getTransactionUpdate,
      updateTransaction,
      applyTransactionUpdates,
      // Bulk Updates
      allowBulkUpdate: allowBulkEdits && !isUpdatingInline && !updating,
      isBulkSelecting,
      selectedTransactions,
      unselectTransaction,
      toggleTransaction,
      selectTransaction,
      applyBulkUpdates,
    }),
    [
      allowBulkEdits,
      allowEdits,
      applyBulkUpdates,
      applyTransactionUpdates,
      clearUpdates,
      editing,
      getTransactionUpdate,
      isBulkSelecting,
      isUpdatingInline,
      selectTransaction,
      selectedTransactions,
      toggleTransaction,
      transactionUpdates,
      unselectTransaction,
      updateTransaction,
      updating,
    ]
  )

  return (
    <TransactionsUpdatesContext.Provider value={context}>
      <TransactionsUpdatesHeader />
      <BulkTransactionUpdates variant={variant} />
      {children}
    </TransactionsUpdatesContext.Provider>
  )
}

function useTransactionUpdates() {
  const [transactionUpdates, setTransactionUpdates] = React.useState<TransactionUpdates>(
    () => new Map()
  )

  const getTransactionUpdate = React.useCallback(
    (factId: string | undefined) => {
      if (!factId) return undefined
      return transactionUpdates.get(factId)
    },
    [transactionUpdates]
  )

  const updateTransaction = React.useCallback((newUpdates: TransactionUpdate) => {
    const { transaction } = newUpdates
    if (!transaction.factId) {
      TrackJS?.console.warn("updateTransaction without factId")
      return
    }

    setTransactionUpdates((prevUpdates) => {
      const newMap = new Map(prevUpdates)
      if (!transaction.factId) return newMap

      const currentUpdates = newMap.get(transaction.factId) || {}
      newMap.set(transaction.factId, { ...currentUpdates, ...newUpdates, transaction })
      return newMap
    })
  }, [])

  const clearTransactionUpdates = React.useCallback(() => {
    setTransactionUpdates(new Map())
  }, [])

  return {
    isUpdatingInline: !!transactionUpdates.size,
    transactionUpdates,
    getTransactionUpdate,
    updateTransaction,
    clearTransactionUpdates,
  }
}

function useBulkUpdates() {
  const [selectedTransactions, setSelectedTransactions] = React.useState<TransactionSelections>(
    () => new Map()
  )

  const selectTransaction = React.useCallback((transaction: Transaction) => {
    if (!transaction.factId) {
      TrackJS?.console.warn("selectTransaction without factId")
      return
    }

    setSelectedTransactions((prevUpdates) => {
      const newMap = new Map(prevUpdates)
      if (prevUpdates.size >= BULK_STATUS_UPDATE_LIMIT) return newMap
      if (!transaction.factId) return newMap
      newMap.set(transaction.factId, transaction)
      return newMap
    })
  }, [])

  const unselectTransaction = React.useCallback((transaction: Transaction) => {
    if (!transaction.factId) {
      TrackJS?.console.warn("unselectTransaction without factId")
      return
    }

    setSelectedTransactions((prevUpdates) => {
      const newMap = new Map(prevUpdates)
      if (!transaction.factId) return newMap
      newMap.delete(transaction.factId)
      return newMap
    })
  }, [])

  const toggleTransaction = React.useCallback(
    (transaction: Transaction) => {
      if (!transaction.factId) {
        TrackJS?.console.warn("toggleTransaction without factId")
        return
      }

      if (selectedTransactions.has(transaction.factId)) {
        unselectTransaction(transaction)
      } else {
        selectTransaction(transaction)
      }
    },
    [selectedTransactions, selectTransaction, unselectTransaction]
  )

  const clearSelections = React.useCallback(() => {
    setSelectedTransactions(new Map())
  }, [])

  return {
    isBulkSelecting: !!selectedTransactions.size,
    selectedTransactions,
    selectTransaction,
    unselectTransaction,
    toggleTransaction,
    clearSelections,
  }
}
